import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FaBold, FaChevronDown, FaChevronRight, FaClipboard, FaClone, FaCopy, FaEyeDropper, FaFillDrip, FaHighlighter, FaItalic, FaTimes, FaUnderline } from 'react-icons/fa';
import { copiaDoTexto } from '../util/malhaAereo';
import '../css/financeiro-parcela-pagar.css';
import '../css/menu-contexto-malha.css';

const MARGEM = 12;

// A paleta do CKEditor (a mesma da caixa do editor de carta), em hex: é o formato que o Excel do
// Modelo Cliente entende, para o destaque sair igual na planilha do cliente.
export const PALETA_DESTAQUE = [
    { cor: '#000000', rotulo: 'Preto' }, { cor: '#4d4d4d', rotulo: 'Cinza escuro' },
    { cor: '#999999', rotulo: 'Cinza' }, { cor: '#e6e6e6', rotulo: 'Cinza claro' },
    { cor: '#ffffff', rotulo: 'Branco' }, { cor: '#e64d4d', rotulo: 'Vermelho' },
    { cor: '#e6994d', rotulo: 'Laranja' }, { cor: '#e6e64d', rotulo: 'Amarelo' },
    { cor: '#99e64d', rotulo: 'Verde claro' }, { cor: '#4de64d', rotulo: 'Verde' },
    { cor: '#4de699', rotulo: 'Água-marinha' }, { cor: '#4de6e6', rotulo: 'Turquesa' },
    { cor: '#4d99e6', rotulo: 'Azul claro' }, { cor: '#4d4de6', rotulo: 'Azul' },
    { cor: '#994de6', rotulo: 'Roxo' },
];

// O que liga e desliga na fonte do destaque; cada um é uma chave booleana em `destaques`.
export const FORMATOS_FONTE = [
    { chave: 'negrito',    rotulo: 'Negrito',    Icone: FaBold },
    { chave: 'italico',    rotulo: 'Itálico',    Icone: FaItalic },
    { chave: 'sublinhado', rotulo: 'Sublinhado', Icone: FaUnderline },
];

// Os botões do Destacar, na ordem em que aparecem: a fonte, o fundo e o texto. O fundo vem antes do
// texto. `grupo` é também a chave da cor em `destaques`, nos dois de cor.
const GRUPOS_DESTAQUE = [
    { grupo: 'fonte', rotulo: 'Fonte',        glifo: 'T' },
    { grupo: 'fundo', rotulo: 'Cor de fundo', glifo: <FaFillDrip size={12} /> },
    { grupo: 'texto', rotulo: 'Cor do texto', glifo: 'A' },
];

export const COPIAS = [
    { lados: ['ida'], rotulo: 'Ida' },
    { lados: ['ret'], rotulo: 'Volta' },
    { lados: ['ida', 'ret'], rotulo: 'Ida & Volta' },
];

// O seletor nativo só aceita #rrggbb.
const hexParaSeletor = (cor) => (/^#[0-9a-f]{6}$/i.test(cor || '') ? cor : '#000000');

// O que o Copiar guardou: { itens: [{ campo: valor }], ids, texto } — só os campos dos lados copiados,
// os `_id` das linhas de onde saiu (null no texto que veio de fora) e o texto que foi ao clipboard. Fica
// fora de componente para sobreviver a trocar de malha sem recarregar a página, e para a Nova linha
// (`NovaOpcaoMalhaModal`) colar dele.
let copiaDaMalha = null;
export const lerCopiaDaMalha = () => copiaDaMalha;
export const guardarCopiaDaMalha = (copia) => { copiaDaMalha = copia; };

// O que o Colar cola, lido do clipboard na hora (o navegador pede a permissão na primeira vez). Texto
// igual ao da cópia guardada é ela mesma. Texto diferente é cópia nova, da malha de outra aba ou de um
// Excel onde ela foi colada: com o formato reconhecido pela estrutura (`copiaDoTexto`), passa a ser
// a cópia guardada; sem formato reconhecido, a guardada sai, porque o clipboard tem coisa mais nova que
// ela, e o Colar avisa que o conteúdo é inválido. Sem texto, ou sem acesso ao clipboard, vale a guardada.
// Devolve { copia, invalido }.
export async function lerParaColar() {
    const guardada = lerCopiaDaMalha();
    let texto = null;
    try {
        texto = navigator.clipboard && navigator.clipboard.readText ? await navigator.clipboard.readText() : null;
    } catch (e) {
        texto = null;
    }
    if (!texto || !texto.trim() || (guardada && texto === guardada.texto)) return { copia: guardada, invalido: false };
    const lida = copiaDoTexto(texto);
    if (!lida) {
        guardarCopiaDaMalha(null);
        return { copia: null, invalido: true };
    }
    const copia = { ...lida, ids: null, texto };
    guardarCopiaDaMalha(copia);
    return { copia, invalido: false };
}
export const AVISO_CLIPBOARD_INVALIDO = 'O que está no clipboard não é uma linha copiada da malha: copie as linhas pelo clique direito.';

/**
 * Caixa do clique direito na planilha da malha aérea — só para quem edita (o Aéreo): destacar a
 * célula (ou a linha inteira, na 1ª coluna), copiar a ida, a volta ou as duas, e colar a ida, a volta
 * ou as duas — nas linhas do alvo, pelo Colar, ou em linhas novas, pelo Duplicar. Tudo age no state da
 * tela; o Salvar grava, como toda edição da malha.
 *
 * Destacar abre uma linha com um botão por grupo (fonte, fundo, texto), e cada botão abre o painel
 * dele embaixo, um por vez. Escolher deixa a caixa aberta, para pôr o fundo e o texto de uma vez;
 * copiar e colar fecham. Fecha também no Esc, clicando fora ou rolando. Shift + clique direito abre o
 * menu do navegador.
 *
 * @param {object}   posicao         { x, y } do clique
 * @param {string}   alvo            o que as ações alcançam ("Esta linha", "3 linhas marcadas")
 * @param {string}   rotuloDestaque  "Destacar" ou "Destacar linha"
 * @param {object}   destaque        { texto, fundo, negrito, italico, sublinhado } atual do que se destaca
 * @param {object}   coresUsadas     { texto: [], fundo: [] } já usadas na malha, fora da paleta
 * @param {Function} onDestacar      (tipo 'texto' | 'fundo' ou chave de FORMATOS_FONTE, cor ou true; null para tirar)
 * @param {Function} onCopiar        (lados) — ['ida'], ['ret'] ou ['ida', 'ret']
 * @param {Function} motivoNaoCopiar (lados) → por que aquele Copiar está desabilitado; null = pode copiar
 * @param {Function} onColar         (lados) — ['ida'], ['ret'] ou ['ida', 'ret']; null com o clipboard inválido
 * @param {string}   copiado         o que há para colar ("2 linhas"), ou null
 * @param {boolean}  clipboardInvalido o clipboard tem texto que não é cópia de malha: o Colar fica
 *                                   clicável e avisa (onColar(null))
 * @param {Function} motivoNaoColar  (lados) → por que aquele Colar está desabilitado; null = pode colar.
 *                                   Com `null` no lugar dos lados, o motivo de não haver nada para colar
 * @param {Function} [onDuplicar]    (lados) — como o onColar, mas a cópia vira linhas novas; sem ele, o
 *                                   Duplicar não aparece
 * @param {Function} [motivoNaoDuplicar] (lados) → como o motivoNaoColar, para o Duplicar
 * @param {Function} onPrever        ({ tipo: 'copiar' | 'colar', lados } | null) — o item sob o mouse,
 *                                   para a planilha pintar as células que ele alcança
 * @param {Function} onFechar
 */
export default function MenuContextoMalha({
    posicao, alvo, rotuloDestaque, destaque, coresUsadas, onDestacar, onCopiar, motivoNaoCopiar = () => null,
    onColar, copiado, clipboardInvalido = false, motivoNaoColar, onDuplicar = null, motivoNaoDuplicar, onPrever, onFechar,
}) {
    const caixaRef = useRef(null);
    const fecharRef = useRef(onFechar);
    fecharRef.current = onFechar;
    // O seletor nativo de cor tem ouvinte próprio, ligado uma vez só: sem a ref ele chamava o
    // onDestacar da primeira renderização, com as opções de quando o menu abriu, e o destaque montado
    // a partir delas apagava o que se tinha posto depois (o negrito, a cor do texto).
    const destacarRef = useRef(onDestacar);
    destacarRef.current = onDestacar;
    // Toda abertura começa com as seções fechadas, e só uma fica aberta: abrir uma fecha a outra. Dentro
    // do Destacar vale o mesmo para os grupos, que voltam fechados toda vez que ele abre.
    const [secao, setSecao] = useState(null);
    const [grupo, setGrupo] = useState(null);
    const [lugar, setLugar] = useState(null);

    // Posiciona depois de medir, para a caixa não sair da tela perto da borda. Reposiciona ao abrir
    // e fechar uma seção, que muda a altura.
    useLayoutEffect(() => {
        const caixa = caixaRef.current;
        if (!caixa) return;
        setLugar({
            left: Math.max(MARGEM, Math.min(posicao.x, window.innerWidth - caixa.offsetWidth - MARGEM)),
            top: Math.max(MARGEM, Math.min(posicao.y, window.innerHeight - caixa.offsetHeight - MARGEM)),
        });
    }, [posicao, secao, grupo]);

    useEffect(() => {
        const dentro = (alvoDoEvento) => caixaRef.current && caixaRef.current.contains(alvoDoEvento);
        const aoClicar = (e) => { if (!dentro(e.target)) fecharRef.current(); };
        const aoRolar = (e) => { if (!dentro(e.target)) fecharRef.current(); };
        const aoRedimensionar = () => fecharRef.current();
        const aoTeclar = (e) => { if (e.key === 'Escape') fecharRef.current(); };
        document.addEventListener('mousedown', aoClicar);
        document.addEventListener('keydown', aoTeclar);
        window.addEventListener('scroll', aoRolar, true);
        window.addEventListener('resize', aoRedimensionar);
        return () => {
            document.removeEventListener('mousedown', aoClicar);
            document.removeEventListener('keydown', aoTeclar);
            window.removeEventListener('scroll', aoRolar, true);
            window.removeEventListener('resize', aoRedimensionar);
        };
    }, []);

    const alternarSecao = (nova) => {
        setSecao(secao === nova ? null : nova);
        setGrupo(null);
    };
    const alternarGrupo = (novo) => setGrupo(grupo === novo ? null : novo);

    // O painel de uma cor ('fundo' ou 'texto'). A `key` remonta o painel ao trocar de um para o outro:
    // reaproveitado, o seletor de "Outra cor" seguiria ligado à cor do primeiro.
    const paleta = (tipo, rotulo) => {
        const atual = destaque?.[tipo] || null;
        const doUsuario = (coresUsadas?.[tipo] ?? []).filter(cor => !PALETA_DESTAQUE.some(c => c.cor === cor));
        return (
            <div key={tipo}>
                <div className="fin-pp__mctx-label">{rotulo}</div>
                <div className="fin-pp__mctx-swatches">
                    <button type="button" title="Sem cor" aria-label="Sem cor"
                        className={`fin-pp__mctx-swatch${!atual ? ' is-active' : ''}`}
                        onClick={() => onDestacar(tipo, null)}>
                        <FaTimes size={8} />
                    </button>
                    {PALETA_DESTAQUE.map(c => (
                        <button key={c.cor} type="button" title={c.rotulo} aria-label={c.rotulo}
                            className={`fin-pp__mctx-swatch${atual === c.cor ? ' is-active' : ''}`}
                            style={{ background: c.cor }}
                            onClick={() => onDestacar(tipo, c.cor)} />
                    ))}
                </div>
                {/* Outra cor: o seletor nativo do navegador. Só no `change`, ao confirmar: o onChange do
                    React dispara a cada arrasto dentro do seletor. As cores que a malha já usa ficam ao
                    lado, como no editor de carta. */}
                <div className="fin-pp__mctx-swatches" style={{ marginTop: 4 }}>
                    <span className="fin-pp__mctx-swatch fin-pp__mctx-cor" title="Outra cor">
                        <FaEyeDropper size={9} />
                        <input type="color" aria-label="Outra cor"
                            defaultValue={hexParaSeletor(atual)}
                            ref={(el) => {
                                if (!el || el.dataset.ligado) return;
                                el.dataset.ligado = '1';
                                el.addEventListener('change', (e) => destacarRef.current(tipo, e.target.value));
                            }} />
                    </span>
                    {doUsuario.map(cor => (
                        <button key={cor} type="button" title={cor} aria-label={cor}
                            className={`fin-pp__mctx-swatch${atual === cor ? ' is-active' : ''}`}
                            style={{ background: cor }}
                            onClick={() => onDestacar(tipo, cor)} />
                    ))}
                </div>
            </div>
        );
    };
    // Mouse ou foco num item de copiar ou colar: a planilha pinta as células que ele alcança.
    const prever = (previa) => ({
        onMouseEnter: () => onPrever && onPrever(previa),
        onMouseLeave: () => onPrever && onPrever(null),
        onFocus: () => onPrever && onPrever(previa),
        onBlur: () => onPrever && onPrever(null),
    });
    // Colar e Duplicar: o mesmo item, com os lados da cópia. O Colar cai nas linhas do alvo, e a planilha pinta onde
    // (`comPrevia`); o Duplicar cria linhas novas, sem onde pintar. Com o clipboard inválido, o item fica clicável e
    // avisa, em vez de agir com a cópia velha.
    const itemDaCopia = ({ chave, rotulo, Icone, onAcao, motivoDe, comPrevia = false }) => (
        <>
            <button type="button" role="menuitem" aria-expanded={secao === chave}
                className={`fin-pp__mctx-item${secao === chave ? ' is-open' : ''}`}
                disabled={!copiado && !clipboardInvalido} title={copiado || clipboardInvalido ? undefined : motivoDe(null) || undefined}
                onClick={() => (clipboardInvalido ? onAcao(null) : alternarSecao(chave))}>
                <span className="fin-pp__mctx-item-ic"><Icone size={12} /></span>
                <span className="fin-pp__mctx-item-txt">
                    {rotulo}
                    {clipboardInvalido
                        ? <span className="fin-pp__mctx-item-sub">conteúdo do clipboard não reconhecido</span>
                        : copiado && <span className="fin-pp__mctx-item-sub">{copiado}</span>}
                </span>
                {copiado && !clipboardInvalido && (secao === chave ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />)}
            </button>
            {copiado && !clipboardInvalido && secao === chave && (
                <div className="fin-pp__mctx-secao">
                    <div className="fin-pp__mctx-row">
                        {COPIAS.map(c => {
                            const motivo = motivoDe(c.lados);
                            return (
                                <button key={c.rotulo} type="button" role="menuitem" className="fin-pp__mctx-btn"
                                    disabled={!!motivo} title={motivo || undefined}
                                    {...(motivo || !comPrevia ? {} : prever({ tipo: 'colar', lados: c.lados }))}
                                    onClick={() => onAcao(c.lados)}>
                                    {c.rotulo}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div
            ref={caixaRef}
            className="fin-pp fin-pp__mctx"
            role="menu"
            aria-label="Ações da célula"
            style={{
                left: lugar ? lugar.left : posicao.x,
                top: lugar ? lugar.top : posicao.y,
                visibility: lugar ? 'visible' : 'hidden',
            }}
            onContextMenu={(e) => e.preventDefault()}>
            <div className="fin-pp__mctx-alvo">{alvo}</div>
            <div className="fin-pp__mctx-body">
                <button type="button" role="menuitem" aria-expanded={secao === 'destacar'}
                    className={`fin-pp__mctx-item${secao === 'destacar' ? ' is-open' : ''}`}
                    onClick={() => alternarSecao('destacar')}>
                    <span className="fin-pp__mctx-item-ic"><FaHighlighter size={12} /></span>
                    <span className="fin-pp__mctx-item-txt">{rotuloDestaque}</span>
                    {secao === 'destacar' ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                </button>
                {/* Aberto, o Destacar mostra uma linha com um botão por grupo, e cada um abre o painel
                    dele embaixo. A barra embaixo do balde e do A é a cor que já está no destaque. */}
                {secao === 'destacar' && (
                    <div className="fin-pp__mctx-secao">
                        <div className="fin-pp__mctx-grupos">
                            {GRUPOS_DESTAQUE.map(g => {
                                const cor = g.grupo === 'fonte' ? null : destaque?.[g.grupo];
                                return (
                                    <button key={g.grupo} type="button" title={g.rotulo} aria-label={g.rotulo}
                                        aria-expanded={grupo === g.grupo}
                                        className={`fin-pp__mctx-grupo${grupo === g.grupo ? ' is-open' : ''}`}
                                        onClick={() => alternarGrupo(g.grupo)}>
                                        {g.glifo}
                                        <span className={`fin-pp__mctx-barra${cor ? ' is-cor' : ''}`} style={cor ? { background: cor } : undefined} />
                                    </button>
                                );
                            })}
                        </div>
                        {grupo === 'fonte' && (
                            <div>
                                <div className="fin-pp__mctx-label">Fonte</div>
                                <div className="fin-pp__mctx-row">
                                    {FORMATOS_FONTE.map(({ chave, rotulo, Icone }) => (
                                        <button key={chave} type="button" aria-pressed={!!destaque?.[chave]} title={rotulo} aria-label={rotulo}
                                            className={`fin-pp__mctx-btn${destaque?.[chave] ? ' is-active' : ''}`}
                                            onClick={() => onDestacar(chave, destaque?.[chave] ? null : true)}>
                                            <Icone size={11} />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(grupo === 'fundo' || grupo === 'texto') && paleta(grupo, GRUPOS_DESTAQUE.find(g => g.grupo === grupo).rotulo)}
                    </div>
                )}

                {/* Copiar a ida, a volta ou as duas, como o Colar. */}
                <button type="button" role="menuitem" aria-expanded={secao === 'copiar'}
                    className={`fin-pp__mctx-item${secao === 'copiar' ? ' is-open' : ''}`}
                    onClick={() => alternarSecao('copiar')}>
                    <span className="fin-pp__mctx-item-ic"><FaCopy size={12} /></span>
                    <span className="fin-pp__mctx-item-txt">Copiar</span>
                    {secao === 'copiar' ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                </button>
                {secao === 'copiar' && (
                    <div className="fin-pp__mctx-secao">
                        <div className="fin-pp__mctx-row">
                            {COPIAS.map(c => {
                                const motivo = motivoNaoCopiar(c.lados);
                                return (
                                    <button key={c.rotulo} type="button" role="menuitem" className="fin-pp__mctx-btn"
                                        disabled={!!motivo} title={motivo || undefined}
                                        {...(motivo ? {} : prever({ tipo: 'copiar', lados: c.lados }))}
                                        onClick={() => onCopiar(c.lados)}>
                                        {c.rotulo}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {itemDaCopia({ chave: 'colar', rotulo: 'Colar', Icone: FaClipboard, onAcao: onColar, motivoDe: motivoNaoColar, comPrevia: true })}
                {onDuplicar && itemDaCopia({ chave: 'duplicar', rotulo: 'Duplicar', Icone: FaClone, onAcao: onDuplicar, motivoDe: motivoNaoDuplicar })}
            </div>
            <div className="fin-pp__mctx-hint">Shift + clique direito abre o menu do navegador</div>
        </div>
    );
}
